import { useTranslation } from 'react-i18next';
import { type RefinementListProps, useClearRefinements, useRefinementList } from 'react-instantsearch';
import clsx from 'clsx';
import { type MenuItem } from 'instantsearch.js/es/connectors/menu/connectMenu';

import { RadioGroup, RadioGroupItem } from '~/vendor/cxy/components/RadioGroup';

import styles from './styles/BetTypeFilters.module.css';

export interface BetTypeFilterProps extends RefinementListProps {
  isDisabled?: boolean;
  label?: string;
}

export function BetTypeFilter({ isDisabled = false, ...props }: BetTypeFilterProps) {
  const { refine: clearRefinement } = useClearRefinements({
    includedAttributes: ['marketDisplayName'],
  });
  const { items, refine } = useRefinementList(props);

  const { t } = useTranslation();

  const handleValueChange = (value: string) => {
    clearRefinement();
    if (value.includes('Total')) {
      const newValues = value.replace('Total::', '').split('::');
      newValues.forEach(newValue => {
        refine(newValue);
      });
    } else if (value !== 'all') {
      const newValue = value === 'all' ? '' : value;
      refine(newValue);
    }
  };

  if (!items.length) return null;

  const groupedBetTypes = groupMenuItems(items, t('CX_PLUS_TOTAL'));

  return (
    <RadioGroup
      className={clsx(styles.betTypeFilters, styles.select)}
      defaultValue={'all'}
      disabled={isDisabled}
      onValueChange={handleValueChange}
    >
      {groupedBetTypes.length > 1 && (
        <RadioGroupItem id="all" key="all" value="all">
          All
        </RadioGroupItem>
      )}
      {groupedBetTypes.map(({ isRefined, label, value }) => (
        <RadioGroupItem checked={isRefined} className={styles.select} id={value} key={value} value={value}>
          {label}
        </RadioGroupItem>
      ))}
    </RadioGroup>
  );
}

function groupMenuItems(items: MenuItem[], totalLabel: string) {
  return items.reduce((agg, item) => {
    if (!item.label.includes('Total')) {
      return [...agg, item];
    } else if (agg.filter(aggItem => aggItem.label === 'Total').length > 0) {
      const index = agg.findIndex(aggItem => aggItem.label === 'Total');
      agg.splice(index, 1, {
        ...agg[index],
        value: `${agg[index].value}::${item.value}`,
      });
      return agg;
    }
    return [
      ...agg,
      {
        ...item,
        label: totalLabel,
        value: `Total::${item.value}`,
      },
    ];
  }, [] as MenuItem[]);
}
